import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetAllProjectsQuery, useDeleteProjectMutation, useBulkDeleteProjectsMutation, useGetAllCategoriesQuery, Project } from '../../store/slices/apiSlice';
import { RootState } from '../../store';
import { Building2, DollarSign, Target, Edit, Trash2, Plus, Eye, Trophy, CheckCircle2, Search, RotateCcw, Filter, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const AdminProjects: React.FC = () => {
  const itemsPerPage = 10;

  const { data: projectsResponse, isLoading: projectsLoading } = useGetAllProjectsQuery({});
  const { data: categoriesResponse } = useGetAllCategoriesQuery();
  const [deleteProject, { isLoading: isDeletingProject }] = useDeleteProjectMutation();
  const projects = projectsResponse?.data || [];
  const categories = categoriesResponse?.data || [];
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  // Selection & Bulk Delete States
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const [currentPageProjects, setCurrentPageProjects] = useState<Project[]>([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [bulkDeleteType, setBulkDeleteType] = useState<'soft' | 'hard'>('soft');
  const [bulkDeleteProjects, { isLoading: isBulkDeleting }] = useBulkDeleteProjectsMutation();

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fundingFilter, setFundingFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  React.useEffect(() => {
    setSelectedProjectIds([]);
  }, [searchTerm, categoryFilter, statusFilter, fundingFilter, dateFilter, projectsResponse]);

  const handleSelectProject = (projectId: number, checked: boolean) => {
    if (checked) {
      setSelectedProjectIds(prev => [...prev, projectId]);
    } else {
      setSelectedProjectIds(prev => prev.filter(id => id !== projectId));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteProjects({
        project_ids: selectedProjectIds,
        delete_type: bulkDeleteType
      }).unwrap();

      toast.success(
        `Successfully ${bulkDeleteType === 'soft' ? 'soft-deleted' : 'hard-deleted'} ${selectedProjectIds.length} ${selectedProjectIds.length === 1 ? 'project' : 'projects'}.`
      );
      setSelectedProjectIds([]);
      setIsBulkDeleteModalOpen(false);
    } catch (error: any) {
      console.error('Bulk delete failed:', error);
      const errorMessage = error?.data?.message || 'Failed to complete deletion. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setStatusFilter('all');
    setFundingFilter('all');
    setDateFilter('all');
    setStartDate('');
    setEndDate('');
  };

  const hasActiveFilters = searchTerm !== '' || categoryFilter !== 'all' || statusFilter !== 'all' || fundingFilter !== 'all' || dateFilter !== 'all';

  const filteredProjects = projects.filter(project => {
    const searchMatch = !searchTerm || 
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const categoryMatch = categoryFilter === 'all' || project.category?.category_id.toString() === categoryFilter;
    
    const statusMatch = statusFilter === 'all' || project.status === statusFilter;
    
    const isFullyFunded = (project.amount_raised || 0) >= project.funding_goal;
    const fundingMatch = fundingFilter === 'all' || 
      (fundingFilter === 'fully' && isFullyFunded) ||
      (fundingFilter === 'partially' && !isFullyFunded);

    // Date Filtering Logic
    let dateMatch = true;
    if (dateFilter !== 'all') {
      const transactionDate = new Date(project.created_at);
      const now = new Date();
      
      if (dateFilter === 'today') {
        dateMatch = transactionDate.toDateString() === now.toDateString();
      } else if (dateFilter === 'week') {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(now.getDate() - 7);
        dateMatch = transactionDate >= oneWeekAgo;
      } else if (dateFilter === 'month') {
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(now.getMonth() - 1);
        dateMatch = transactionDate >= oneMonthAgo;
      } else if (dateFilter === 'custom') {
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          dateMatch = transactionDate >= start && transactionDate <= end;
        }
      }
    }

    return searchMatch && categoryMatch && statusMatch && fundingMatch && dateMatch;
  });

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    ...categories.map(cat => ({ value: cat.category_id.toString(), label: cat.name }))
  ];

  const statusOptions = [
    { value: 'all', label: 'All Project Status' },
    { value: 'active', label: 'Active' },
    { value: 'draft', label: 'Draft' },
    { value: 'closed', label: 'Closed' },
  ];

  const fundingOptions = [
    { value: 'all', label: 'All Funding' },
    { value: 'fully', label: 'Fully Funded' },
    { value: 'partially', label: 'Still Funding' },
  ];

  const formatCurrency = (amount: number | string | null | undefined, currencySymbol: string = '₦') => {
    const value = parseFloat(String(amount || '0')) || 0;
    return `${currencySymbol}${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)}`;
  };

  const handleEditProject = (project: Project) => {
    navigate('/admin/update-project', { state: { project } });
  };

  const handleViewProject = (project: Project) => {
    navigate('/admin/projects/view', { state: { project } });
  };

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedProjectTitle, setSelectedProjectTitle] = useState('');

  const handleDeleteProject = async (projectId: string) => {
    const project = projects.find(p => p.project_id.toString() === projectId);
    setSelectedProjectId(projectId);
    setSelectedProjectTitle(project?.title || '');
    setIsDeleteConfirmationOpen(true);
  };

  const confirmDeleteProject = async () => {
    // Check if user is authenticated
    const token = localStorage.getItem('diasporawise_admin_token');
    if (!token) {
      toast.error('You are not authenticated. Please login again.');
      setIsDeleteConfirmationOpen(false);
      return;
    }

    // Validate token format
    if (token.length < 10) {
      toast.error('Invalid authentication token. Please login again.');
      setIsDeleteConfirmationOpen(false);
      return;
    }

    // Check if token looks like a JWT (has dots)
    if (!token.includes('.')) {
      toast.error('Invalid token format. Please login again.');
      setIsDeleteConfirmationOpen(false);
      return;
    }

    try {
      const result = await deleteProject({ project_id: parseInt(selectedProjectId) }).unwrap();
      if (result.success) {
        toast.success('Project deleted successfully!');
      } else {
        toast.error(`Failed to delete project: ${result.message}`);
      }
    } catch (error: any) {
      console.error('Error deleting project:', error);
      toast.error(error?.data?.message || 'An error occurred while deleting the project. Please try again.');
    }
    setIsDeleteConfirmationOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border border-green-200';
      case 'funded': return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'closed': return 'bg-gray-100 text-gray-800 border border-gray-200';
      case 'draft': return 'bg-amber-100 text-amber-800 border border-amber-200';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const tableColumns: any[] = [];

  if (user?.role === 'admin' || user?.role === 'manager') {
    tableColumns.push({
      key: 'selection',
      label: (
        <input
          type="checkbox"
          checked={currentPageProjects.length > 0 && currentPageProjects.every(row => selectedProjectIds.includes(row.project_id))}
          onChange={(e) => {
            const checked = e.target.checked;
            if (checked) {
              setSelectedProjectIds(prev => {
                const newSelections = [...prev];
                currentPageProjects.forEach(row => {
                  if (row.project_id !== undefined && !newSelections.includes(row.project_id)) {
                    newSelections.push(row.project_id);
                  }
                });
                return newSelections;
              });
            } else {
              const currentPageIds = currentPageProjects.map(row => row.project_id);
              setSelectedProjectIds(prev => prev.filter(id => !currentPageIds.includes(id)));
            }
          }}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
        />
      ),
      render: (_: any, row: Project) => (
        <input
          type="checkbox"
          checked={selectedProjectIds.includes(row.project_id)}
          onChange={(e) => handleSelectProject(row.project_id, e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
        />
      ),
    });
  }

  tableColumns.push(
    {
      key: 'sno',
      label: 'S.No',
      render: (_: any, __: any, index: number) => (
        <span className="font-bold text-gray-400 text-xs">#{index + 1}</span>
      ),
    },
    {
      key: 'image',
      label: 'Image',
      render: (_: any, row: Project) => {
        const isFullyFunded = Number(row.amount_raised || 0) >= Number(row.funding_goal);
        return (
          <div className="relative w-16 h-12 sm:w-20 sm:h-14 min-w-[64px] min-h-[48px] sm:min-w-[80px] sm:min-h-[56px]">
            {row.images && row.images.length > 0 ? (
              <img
                src={row.images[0].image_url}
                alt={row.title}
                className={`w-full h-full object-cover rounded shadow border ${isFullyFunded ? 'border-green-500' : 'border-gray-200'}`}
              />
            ) : (
              <div className={`w-full h-full rounded shadow border flex items-center justify-center bg-blue-50 border-blue-100 text-blue-500 ${isFullyFunded ? 'border-green-500' : 'border-blue-200'}`}>
                <Building2 className="w-6 h-6 sm:w-8 sm:h-8" />
              </div>
            )}
            {isFullyFunded && (
              <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1 shadow-lg border-2 border-white">
                <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4" />
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'title',
      label: 'Project Title',
      sortable: true,
      render: (value: string, row: Project) => {
        const isFullyFunded = Number(row.amount_raised || 0) >= Number(row.funding_goal);
        return (
          <div className="flex flex-col">
            <div className="font-bold text-gray-900 text-sm sm:text-base flex items-center gap-2">
              {value}
              {isFullyFunded && (
                <span className="flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700 border border-green-200 uppercase tracking-wider">
                  <Trophy className="w-3 h-3 mr-1" />
                  Fully Funded
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      render: (_: any, row: Project) => (
        <span className="capitalize text-sm sm:text-base">{row.category?.name || 'Unknown'}</span>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
    },
    {
      key: 'funding_goal',
      label: 'Target Amount',
      sortable: true,
      render: (value: number, row: Project) => formatCurrency(value, row.currency_symbol),
    },
    {
      key: 'amount_raised',
      label: 'Raised Amount',
      sortable: true,
      render: (value: number | null, row: Project) => {
        const raised = value || 0;
        const percentage = row.funding_goal > 0 ? (raised / row.funding_goal) * 100 : 0;
        const isFullyFunded = Number(raised) >= Number(row.funding_goal);
        
        return (
          <div className="w-full min-w-[100px]">
            <div className={`font-bold text-sm sm:text-base ${isFullyFunded ? 'text-green-600' : 'text-gray-900'}`}>
              {formatCurrency(raised, row.currency_symbol)}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-1000 ${isFullyFunded ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : 'bg-blue-500'}`}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                />
              </div>
              <span className={`text-xs font-bold ${isFullyFunded ? 'text-green-600' : 'text-gray-500'}`}>
                {percentage.toFixed(1)}%
              </span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value: string, row: Project) => {
        const isFullyFunded = Number(row.amount_raised || 0) >= Number(row.funding_goal);
        const displayValue = isFullyFunded ? 'Funded' : value;
        const colorClass = isFullyFunded ? 'bg-green-600 text-white shadow-sm ring-1 ring-green-700/10' : getStatusColor(value);
        
        return (
          <span className={`inline-flex px-2 py-1 text-[10px] sm:text-xs font-bold rounded-full uppercase tracking-wide transition-all ${colorClass}`}>
            {isFullyFunded && <CheckCircle2 className="w-3 h-3 mr-1" />}
            {displayValue}
          </span>
        );
      },
    },
    {
      key: 'deadline',
      label: 'Deadline',
      sortable: true,
      render: (value: string) => (
        <span className="text-sm sm:text-base">{formatDateUtil(value)}</span>
      ),
    },
  );

  if (user?.role === 'admin' || user?.role === 'manager') {
    tableColumns.push({
      key: 'actions',
      label: 'Actions',
      render: (_: any, row: Project) => (
        <div className="flex space-x-1 sm:space-x-2">
          <Button
            onClick={() => handleViewProject(row)}
            variant="outline"
            size="sm"
            icon={Eye}
            disabled={isDeletingProject}
            className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200 hover:border-green-300 hover:shadow-sm"
          >
            {''}
          </Button>
          <Button
            onClick={() => handleEditProject(row)}
            variant="outline"
            size="sm"
            icon={Edit}
            disabled={isDeletingProject}
            className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200 hover:border-green-300 hover:shadow-sm"
          >
            {''}
          </Button>
          <Button
            onClick={() => handleDeleteProject(row.project_id.toString())}
            variant="outline"
            size="sm"
            icon={Trash2}
            disabled={isDeletingProject}
          >
            {''}
          </Button>
        </div>
      ),
    });
  }

  return (
    <div className="space-y-6">

        {/* Premium Filtering Hub */}
        <Card className="p-6 mb-8 bg-white border-none shadow-xl rounded-2xl overflow-visible">
          <div className="flex flex-col space-y-6">
            {/* Top Row: Search and Actions */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="relative flex-1 group min-w-[300px]">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                <Input
                  placeholder="Search projects by title or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-14 bg-gray-50/50 border-gray-200 focus:bg-white transition-all shadow-inner rounded-2xl text-base"
                />
              </div>

              <div className="flex items-center gap-4">
                {hasActiveFilters && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleResetFilters}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 h-12 px-6 font-bold text-xs uppercase tracking-widest transition-all shadow-sm active:scale-95"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset
                  </Button>
                )}
                {(user?.role === 'admin' || user?.role === 'manager') && (
                  <Button 
                    variant="primary" 
                    size="sm" 
                    icon={Plus} 
                    onClick={() => navigate('/admin/create-project')}
                    className="h-12 px-8 bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 rounded-xl"
                  >
                    Create Project
                  </Button>
                )}
              </div>
            </div>

            {/* Filter Selectors Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
              <div className="space-y-2">
                <label className="flex items-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                  <Filter className="w-3 h-3 mr-1.5" /> Project Category
                </label>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  options={categoryOptions}
                  className="h-12 text-sm font-semibold border-gray-200 bg-gray-50 focus:bg-white transition-all rounded-xl shadow-sm hover:border-blue-300"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                  <CheckCircle2 className="w-3 h-3 mr-1.5" /> Project Status
                </label>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  options={statusOptions}
                  className="h-12 text-sm font-semibold border-gray-200 bg-gray-50 focus:bg-white transition-all rounded-xl shadow-sm hover:border-blue-300"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                  <Trophy className="w-3 h-3 mr-1.5" /> Funding Integrity
                </label>
                <Select
                  value={fundingFilter}
                  onChange={(e) => setFundingFilter(e.target.value)}
                  options={fundingOptions}
                  className="h-12 text-sm font-semibold border-gray-200 bg-gray-50 focus:bg-white transition-all rounded-xl shadow-sm hover:border-blue-300"
                />
              </div>
            </div>

            {/* Date Filters Row */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6 pt-6 border-t border-gray-100">
              <div className="flex flex-wrap gap-2.5">
                {[
                  { id: 'all', label: 'All Time' },
                  { id: 'today', label: 'Today' },
                  { id: 'week', label: 'This Week' },
                  { id: 'month', label: 'This Month' },
                  { id: 'custom', label: 'Custom Range' }
                ].map((period) => (
                  <button
                    key={period.id}
                    onClick={() => setDateFilter(period.id)}
                    className={`px-6 py-2.5 rounded-full text-[11px] font-black uppercase tracking-widest transition-all duration-300 active:scale-95 ${
                      dateFilter === period.id
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                        : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-900 border border-transparent'
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>

              {dateFilter === 'custom' && (
                <div className="flex items-center gap-4 animate-in fade-in slide-in-from-left-4 duration-500">
                  <div className="relative group">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="h-11 px-5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm hover:border-blue-200"
                    />
                    <div className="absolute -top-6 left-1 text-[9px] font-black text-blue-600/60 uppercase tracking-widest">From</div>
                  </div>
                  <div className="w-2 h-[2px] bg-gray-200 rounded-full" />
                  <div className="relative group">
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="h-11 px-5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm hover:border-blue-200"
                    />
                    <div className="absolute -top-6 left-1 text-[9px] font-black text-blue-600/60 uppercase tracking-widest">To</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Results Info */}
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em]">Project Archive</h3>
            <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100 font-bold px-3 py-1 rounded-full">
              {filteredProjects.length} Projects
            </Badge>
          </div>
        </div>

        {projectsLoading ? (
          <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <section aria-label="Project List" className="bg-white/50 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/20">
            <Table
              columns={tableColumns}
              data={filteredProjects}
              searchable={false}
              itemsPerPage={itemsPerPage}
              getRowClassName={(row: Project) => {
                const isFullyFunded = Number(row.amount_raised || 0) >= Number(row.funding_goal);
                return isFullyFunded 
                  ? 'bg-green-50/40 hover:bg-green-100/60 transition-colors duration-300' 
                  : 'hover:bg-gray-50/80 transition-colors duration-200';
              }}
              onPageDataChange={setCurrentPageProjects}
            />
          </section>
        )}
        
        {!projectsLoading && filteredProjects.length === 0 && (
          <div className="text-center py-20 bg-white/50 backdrop-blur-sm rounded-2xl border border-white/20">
            <Building2 className="w-16 h-16 text-gray-200 mx-auto mb-6" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Projects Found</h3>
            <p className="text-gray-500 mb-8 max-w-xs mx-auto">We couldn't find any projects matching your current filters. Try adjusting your search or clearing filters.</p>
            {hasActiveFilters && (
              <Button onClick={handleResetFilters} variant="outline" size="sm" className="font-bold border-gray-300">
                Clear All Filters
              </Button>
            )}
          </div>
        )}

      <Modal
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        title="Delete Project"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete the project{' '}
            <span className="font-semibold">{selectedProjectTitle}</span>?
            This action cannot be undone.
          </p>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setIsDeleteConfirmationOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteProject}
              disabled={isDeletingProject}
            >
              {isDeletingProject ? 'Deleting...' : 'Delete Project'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Floating Bulk Actions Bar */}
      {selectedProjectIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-4 rounded-2xl shadow-2xl z-50 flex items-center gap-6 border border-gray-800 transition-all duration-300 animate-in fade-in slide-in-from-bottom-4">
          <span className="text-sm font-semibold">
            {selectedProjectIds.length} {selectedProjectIds.length === 1 ? 'project' : 'projects'} selected
          </span>
          <div className="h-6 w-px bg-gray-800"></div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setBulkDeleteType('soft');
                setIsBulkDeleteModalOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
            >
              Soft Delete
            </button>
            <button
              type="button"
              onClick={() => {
                setBulkDeleteType('hard');
                setIsBulkDeleteModalOpen(true);
              }}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
            >
              Hard Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedProjectIds([])}
              className="text-gray-400 hover:text-white px-2 py-2 text-xs font-semibold transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        isOpen={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        title={`${bulkDeleteType === 'soft' ? 'Soft' : 'Hard'} Delete Selected Projects`}
      >
        <div className="p-6">
          <p className="text-gray-700 mb-4">
            Are you sure you want to {bulkDeleteType === 'soft' ? 'soft delete' : 'permanently hard delete'}{' '}
            <span className="font-semibold text-gray-900">{selectedProjectIds.length}</span> selected {selectedProjectIds.length === 1 ? 'project' : 'projects'}?
          </p>
          {bulkDeleteType === 'hard' && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs sm:text-sm font-medium mb-6">
              <span className="font-bold uppercase mt-0.5">Warning:</span>
              <span>
                This action is permanent and cannot be undone. All data associated with these projects will be permanently destroyed.
              </span>
            </div>
          )}
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setIsBulkDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant={bulkDeleteType === 'soft' ? 'warning' : 'danger'}
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
            >
              {isBulkDeleting ? 'Deleting...' : `Confirm ${bulkDeleteType === 'soft' ? 'Soft' : 'Hard'} Delete`}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminProjects;